import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UUID } from '@finxone-platform/shared/base-types';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ExchangeTransactionResponse,
  NotificationPayload,
  NotificationTypeEnum,
  PaginationMeta,
  TransactionType,
  transactionStatusMapping,
} from '@finxone-platform/shared/sys-config-types';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Notification } from '@novu/js';
import { Observable, catchError, of, take, tap, throwError } from 'rxjs';
import {
  ClearNotificationsCache,
  FetchNotifications,
  ProcessNotification,
  SetNotificationLoadingStatus,
  SetPaymentRequestSummary,
  SetPaymentRequestSummaryApprovedorRejectedDate,
  SetSelectedNotification,
  UpdateNotificationStatus,
  UpdateNotifications,
} from '../actions/notification.action';
import { AddProgressBarStack, RemoveProgressBarStack } from '../actions/progress-bar.action';
import { AccountService } from '../services/account-service/account-service.service';
import {
  GetBeneficiaryResponse,
  NotificationModel,
  TransactionDetailResponse,
} from '../services/account-service/account.type';
import { ExchangeService } from '../services/exchange-service/exchange-service.service';
import { NotificationService } from '../services/notification-service/notification-service.service';

export interface PaymentRequestSummary {
  id: string;
  amount: string;
  currency: string;
  paymentReference: string;
  paymentReason: string;
  requester: {
    userId: string;
    name: string;
    accountId: string;
    accountName: string;
    destinationIdentifier: {
      accountNumber: string;
      'sort-code': string;
    };
  };
  approver: {
    userId: string;
    name: string;
  };
  beneficiary: {
    name: string;
    destinationIdentifier: {
      accountNumber: string;
      'sort-code': string;
    };
  };
  approverStatus: string;
  approvedOrRejectedDate: string;
  status: string;
  createdDate: string;
  recordId: string;
}
export interface NotificationStateModel {
  isLoading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedNotification: any;
  notificationList: NotificationModel[];
  paymentRequestSummary?: PaymentRequestSummary;
  hasMore?: boolean;
  pagination: PaginationMeta;
}
@State<NotificationStateModel>({
  name: 'notifications',
  defaults: {
    isLoading: false,
    selectedNotification: {},
    notificationList: [],
    hasMore: false,
    pagination: {
      page: 0,
      pageSize: 10,
      totalItems: 0,
      totalPages: 0,
    },
  },
})
@Injectable()
export class NotificationState {
  constructor(
    private accountService: AccountService,
    private exchangeService: ExchangeService,
    private router: Router,
    private notificationService: NotificationService,
    private alertHandlerService: AlertHandlerService,
  ) {}

  @Selector()
  static getNotification(state: NotificationStateModel) {
    return state;
  }

  @Selector()
  static getLoadingStatus(state: NotificationStateModel) {
    return state.isLoading;
  }

  @Action(SetNotificationLoadingStatus)
  setLoadingStatus(ctx: StateContext<NotificationStateModel>, action: SetNotificationLoadingStatus) {
    try {
      ctx.patchState({
        ...ctx.getState(),
        isLoading: action.isLoading,
      });
      return true;
    } catch (err) {
      return throwError(() => err);
    }
  }

  @Action(FetchNotifications)
  fetchNotifications(ctx: StateContext<NotificationStateModel>, action: FetchNotifications) {
    ctx.dispatch(new SetNotificationLoadingStatus(true));
    this.notificationService
      .callNovuServiceToFetchNotifications(action.page, action.limit, action.filterType)
      .then((notifications) => {
        if (notifications?.data) {
          const notificationListData = this.transformNotificationsDataToMatchNotificationsModel(
            notifications?.data?.notifications,
          );
          const pagination = {
            page: action.page,
            totalPages: action.page,
            totalItems: notifications?.data?.notifications?.length || 0,
            pageSize: action.limit,
          };
          ctx.dispatch(
            new UpdateNotifications(notificationListData, notifications?.data?.hasMore, pagination),
          );
        }
      })
      .catch((error: Error) => {
        console.error('There has been an error listing notifications:', error);
        this.alertHandlerService.showAlertFn('error', 'There has been an error listing notifications.');
      })
      .finally(() => {
        ctx.dispatch(new SetNotificationLoadingStatus(false));
      });
  }

  private transformNotificationsDataToMatchNotificationsModel(data: Notification[]): NotificationModel[] {
    return (
      data?.map((notification): NotificationModel => {
        return {
          notificationId: new UUID().toString(),
          novuId: notification.id,
          url: notification.redirect?.url ?? '',
          seen: notification.isRead,
          content: notification?.body,
          title: notification?.subject?.length ? notification?.subject : notification?.body,
          createdAt: notification.createdAt,
          txnId: notification.transactionId,
          payload: {
            ...notification.data,
            inAppBody: notification?.body,
            inAppTitle: notification?.subject?.length ? notification?.subject : notification?.body,
            inAppSubject: notification?.subject?.length ? notification?.subject : notification?.body,
          } as NotificationPayload,
          read: notification.isRead,
        };
      }) ?? []
    );
  }

  @Action(ProcessNotification)
  processNotifications(ctx: StateContext<NotificationStateModel>) {
    try {
      const currentUrl = this.router.url;
      let notificationId = '';

      // Check if the URL is defined and has the expected format
      if (!this.isValidUrl(currentUrl)) {
        console.error('Invalid or undefined URL:', currentUrl);
        return of(null);
      }

      const paramData = currentUrl.split('/');
      notificationId = paramData[4] ?? '';

      const notificationState = ctx.getState();
      if (!notificationState) {
        return of(null);
      }

      const notificationItem = notificationState.notificationList.find(
        (stage) => stage.novuId === notificationId,
      );

      ctx.dispatch(new AddProgressBarStack({ uniqueId: 'ProcessNotification' }));

      if (!notificationItem) {
        console.error('Notification item not found for id:', notificationId);
        return of(null);
      }

      const notificationType = notificationItem.payload?.['notificationType'] as NotificationTypeEnum;

      switch (notificationType) {
        case NotificationTypeEnum.TRANSACTION_INCOMING:
        case NotificationTypeEnum.TRANSACTION_OUTGOING:
          return this.handleTransactionNotification(ctx, notificationItem);
        case NotificationTypeEnum.EXCHANGE_COMPLETED:
        case NotificationTypeEnum.EXCHANGE_FAILED:
          return this.handleExchangeNotification(ctx, notificationItem);
        case NotificationTypeEnum.BENEFICIARY_ADDED:
        case NotificationTypeEnum.BENEFICIARY_UPDATED:
        case NotificationTypeEnum.BENEFICIARY_REMOVED:
          return this.handleBeneficiaryAddedNotification(ctx, notificationItem);
        default:
          return this.handleDefaultNotification(ctx, notificationItem);
      }
    } catch (err) {
      ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'ProcessNotification' }));
      return throwError(() => err);
    } finally {
      ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'ProcessNotification' }));
    }
  }

  public isValidUrl(url: string): boolean {
    return typeof url === 'string' && url.includes('/');
  }

  public handleTransactionNotification(
    ctx: StateContext<NotificationStateModel>,
    notificationItem: NotificationModel,
  ): Observable<TransactionDetailResponse> {
    const transactionId =
      notificationItem.payload?.['paymentId'] ?? notificationItem.payload?.['transactionId'] ?? '';
    const transactionType =
      transactionStatusMapping[
        notificationItem.payload?.['notificationType'] ?? NotificationTypeEnum.TRANSACTION_INCOMING
      ] ?? TransactionType.INCOMING;

    return this.accountService.getTransactionDetail(transactionId, transactionType as TransactionType).pipe(
      tap((notificationData: TransactionDetailResponse) => {
        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'ProcessNotification' }));

        ctx.patchState({
          ...ctx.getState(),
          isLoading: false,
          selectedNotification: {
            ...notificationData,
            notificationType: notificationItem.payload['notificationType'],
          },
        });
      }),
      catchError((_err) => {
        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'ProcessNotification' }));
        throw _err;
      }),
    );
  }

  public handleExchangeNotification(
    ctx: StateContext<NotificationStateModel>,
    notificationItem: NotificationModel,
  ): Observable<ExchangeTransactionResponse> {
    const transactionId = notificationItem.payload?.['id'] ?? '';
    return this.exchangeService.getExchangeDetail(transactionId).pipe(
      tap((notificationData: ExchangeTransactionResponse) => {
        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'ProcessNotification' }));

        ctx.patchState({
          ...ctx.getState(),
          isLoading: false,
          selectedNotification: {
            ...notificationData,
            notificationType: notificationItem.payload['notificationType'],
          },
        });
      }),
      catchError((_err) => {
        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'ProcessNotification' }));
        throw _err;
      }),
    );
  }

  public handleDefaultNotification(
    ctx: StateContext<NotificationStateModel>,
    notificationItem: NotificationModel,
  ): Observable<boolean> {
    ctx.patchState({
      ...ctx.getState(),
      isLoading: false,
      selectedNotification: {
        ...notificationItem.payload,
        createdAt: notificationItem.createdAt,
        notificationType: notificationItem.payload['notificationType'],
      },
    });
    return of(true);
  }

  public handleBeneficiaryAddedNotification(
    ctx: StateContext<NotificationStateModel>,
    notificationItem: NotificationModel,
  ): Observable<GetBeneficiaryResponse> {
    const beneficiaryId = notificationItem.payload?.['beneficiaryId'] ?? '';
    return this.accountService.getBeneficiaryDetail(beneficiaryId).pipe(
      tap((notificationData: GetBeneficiaryResponse) => {
        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'ProcessNotification' }));

        ctx.patchState({
          ...ctx.getState(),
          isLoading: false,
          selectedNotification: {
            ...notificationData,
            notificationType: notificationItem.payload['notificationType'],
          },
        });
      }),
      catchError((_err) => {
        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'ProcessNotification' }));
        throw _err;
      }),
    );
  }

  @Action(UpdateNotifications)
  updateNotifications(ctx: StateContext<NotificationStateModel>, action: UpdateNotifications) {
    ctx.setState((state: NotificationStateModel) => {
      return {
        ...state,
        notificationList: this.appendUniqueNotifications(state.notificationList, action.notificationList),
        isLoading: false,
        hasMore: action.hasMore,
        pagination: action.pagination,
      };
    });
  }

  /**
   * Appends new notifications to a previous list, ensuring no duplicate notification.
   *
   * @param previousNotificationList - Existing notifications
   * @param newNotificationList - Notifications to add
   * @returns A combined array of notifications with unique novuIds
   */
  private appendUniqueNotifications(
    previousNotificationList: NotificationModel[] = [],
    newNotificationList: NotificationModel[] = [],
  ): NotificationModel[] {
    const result: NotificationModel[] = [...previousNotificationList];

    // Create a Set of existing IDs
    const existingIds = new Set(previousNotificationList.map((notification) => notification.novuId));

    // Add new notifications only if their IDs are not already in the existing Set
    newNotificationList.forEach((notification) => {
      if (!existingIds.has(notification.novuId)) {
        existingIds.add(notification.novuId);
        result.push(notification);
      }
    });

    return result;
  }

  @Action(SetPaymentRequestSummary)
  addPaymentRequestSummaryInState(
    ctx: StateContext<NotificationStateModel>,
    action: SetPaymentRequestSummary,
  ) {
    ctx.patchState({
      paymentRequestSummary: action.paymentRequestSummary,
    });
  }

  @Action(SetPaymentRequestSummaryApprovedorRejectedDate)
  updatePaymentRequestSummaryDateInState(
    ctx: StateContext<NotificationStateModel>,
    action: SetPaymentRequestSummaryApprovedorRejectedDate,
  ) {
    ctx.setState({
      ...ctx.getState(),
      paymentRequestSummary: {
        ...(ctx.getState().paymentRequestSummary as PaymentRequestSummary),
        approvedOrRejectedDate: action.date,
      },
    });
  }

  @Action(SetSelectedNotification)
  setSelectedNotification(ctx: StateContext<NotificationStateModel>, action: SetSelectedNotification) {
    ctx.patchState({
      selectedNotification: action.selectedNotification,
    });
  }

  @Action(UpdateNotificationStatus)
  updateNotificationStatus(ctx: StateContext<NotificationStateModel>, action: UpdateNotificationStatus) {
    return this.notificationService.updateNotificationStatus(action.notificationStatus).pipe(
      take(1),
      catchError((_err) => {
        throw _err;
      }),
    );
  }

  @Action(ClearNotificationsCache)
  clearAccountCache(ctx: StateContext<NotificationStateModel>) {
    ctx.patchState({
      isLoading: false,
      selectedNotification: {},
      notificationList: [],
    });
  }
}
